import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, Button, Typography, Space } from 'antd';
import { ReloadOutlined, BugOutlined } from '@ant-design/icons';
import { useAppTheme } from '../theme/ThemeProvider';
import { logger } from '../utils/logger';

const { Title, Text, Paragraph } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // 更新 state 使下一次渲染能够显示降级后的 UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 你同样可以将错误日志上报给服务器
    logger.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // 你可以自定义降级后的 UI 并渲染
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback 
          error={this.state.error} 
          errorInfo={this.state.errorInfo}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error?: Error;
  errorInfo?: ErrorInfo;
  onReload: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo, onReload }) => {
  const { theme } = useAppTheme();

  return (
    <Card
      style={{
        margin: '20px',
        borderRadius: 12,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        background: theme.surfaceBackground,
        border: `1px solid ${theme.surfaceBorder}`
      }}
    >
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <BugOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
            <Title level={3} style={{ color: theme.textPrimary, margin: 0 }}>
              组件加载出错
            </Title>
          </div>
          
          <Paragraph style={{ color: theme.textSecondary, margin: 0 }}>
            抱歉，这个组件遇到了问题。请尝试重新加载或联系技术支持。
          </Paragraph>

          {process.env.NODE_ENV === 'development' && error && (
            <Card 
              size="small" 
              style={{ 
                background: theme.toolbarBackground, 
                textAlign: 'left',
                maxHeight: '200px',
                overflow: 'auto'
              }}
            >
              <Text code style={{ fontSize: '12px', color: theme.textSecondary }}>
                {error.message}
              </Text>
              {errorInfo && (
                <div style={{ marginTop: '8px' }}>
                  <Text code style={{ fontSize: '12px', color: theme.textSecondary }}>
                    {errorInfo.componentStack}
                  </Text>
                </div>
              )}
            </Card>
          )}

          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={onReload}
            style={{
              background: theme.accentPrimary,
              borderColor: theme.accentPrimary
            }}
          >
            重新加载组件
          </Button>
        </Space>
      </div>
    </Card>
  );
};

export default ErrorBoundary;
